import { Link } from "react-router-dom";
import { Clock3, Heart, Minus, Plus, ShoppingCart } from "lucide-react";
import { toast } from "sonner";
import { ProductImage } from "@/components/catalog/ProductImage";
import { StarRating } from "@/components/catalog/StarRating";
import { useCart } from "@/features/cart/CartContext";
import { useWishlist } from "@/features/wishlist/WishlistContext";
import type { Product, ProductTag } from "@/features/catalog/types";
import { ROUTES } from "@/constants/routes";
import { cn } from "@/lib/utils";

type ProductCardProps = {
    product: Product;
    className?: string;
};

function formatINR(value: number): string {
    return `₹${value.toLocaleString("en-IN")}`;
}

const TAG_META: Record<ProductTag, { label: string; className: string }> = {
    bestseller: { label: "Bestseller", className: "bg-brand text-white" },
    new: { label: "New", className: "bg-accentbrand text-ink" },
    deal: { label: "Deal", className: "bg-error text-white" },
};

/**
 * ProductCard — Blinkit-style tile with:
 *   • Placeholder image + tag chip + delivery ETA chip
 *   • Wishlist heart (top-right)
 *   • Brand + name + weight
 *   • Rating (count)
 *   • Price + MRP + discount %
 *   • Stock indicator
 *   • Add-to-cart button that transforms into a quantity stepper
 *
 * Clicking the card navigates to product detail; the wishlist and cart
 * controls stop propagation so those actions don't trigger navigation.
 */
export function ProductCard({ product, className }: ProductCardProps) {
    const { getQuantity, add } = useCart();
    const { has, toggle } = useWishlist();

    const qty = getQuantity(product.id);
    const wished = has(product.id);
    const outOfStock = !product.in_stock || product.stock_count <= 0;
    const lowStock = !outOfStock && product.stock_count <= 15;
    const discount = Math.max(
        0,
        Math.round(((product.mrp - product.price) / product.mrp) * 100),
    );
    const tagMeta = product.tag ? TAG_META[product.tag] : null;

    const stop = (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
    };

    const onWishlist = (e: React.MouseEvent) => {
        stop(e);
        const added = toggle(product.id);
        toast(added ? "Added to wishlist" : "Removed from wishlist", {
            description: product.name,
        });
    };

    const onAdd = async (e: React.MouseEvent) => {
        stop(e);
        if (outOfStock) return;
        try {
            await add(product.id, 1);
            toast.success("Added to cart", { description: `${product.name} · ${product.weight}` });
        } catch {
            toast.error("Couldn't add to cart", {
                description: "Please try again in a moment.",
            });
        }
    };

    const onDec = async (e: React.MouseEvent) => {
        stop(e);
        try {
            await add(product.id, -1);
        } catch {
            toast.error("Couldn't update cart", { description: "Please try again." });
        }
    };

    const onInc = async (e: React.MouseEvent) => {
        stop(e);
        if (qty >= product.stock_count) {
            toast.error("You've reached the stock limit for this item.");
            return;
        }
        try {
            await add(product.id, 1);
        } catch {
            toast.error("Couldn't update cart", { description: "Please try again." });
        }
    };

    return (
        <Link
            to={ROUTES.productDetailsBuild(product.slug || product.id)}
            data-testid={`product-card-${product.id}`}
            className={cn(
                "group flex h-full flex-col overflow-hidden rounded-2xl border border-border/70 bg-card transition hover:-translate-y-0.5 hover:shadow-elevated",
                outOfStock && "opacity-80",
                className,
            )}
        >
            {/* Image + chips */}
            <div className="relative aspect-square w-full">
                <ProductImage
                    product={product}
                    size="card"
                    className="h-full w-full"
                />
                {tagMeta ? (
                    <span
                        className={cn(
                            "absolute left-2 top-2 rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide",
                            tagMeta.className,
                        )}
                        data-testid={`product-tag-${product.id}`}
                    >
                        {tagMeta.label}
                    </span>
                ) : null}
                <span
                    className="absolute left-2 bottom-2 inline-flex items-center gap-1 rounded-full bg-white/90 px-2 py-0.5 text-[10px] font-semibold text-foreground backdrop-blur"
                    data-testid={`product-eta-${product.id}`}
                >
                    <Clock3 className="h-3 w-3" />
                    {product.eta_minutes} min
                </span>
                <button
                    type="button"
                    onClick={onWishlist}
                    aria-label={wished ? `Remove ${product.name} from wishlist` : `Add ${product.name} to wishlist`}
                    aria-pressed={wished}
                    data-testid={`product-wishlist-${product.id}`}
                    className={cn(
                        "absolute right-2 top-2 grid h-9 w-9 place-items-center rounded-full border transition",
                        wished
                            ? "border-error bg-error text-white"
                            : "border-white/60 bg-white/80 text-foreground/70 hover:bg-white hover:text-error backdrop-blur",
                    )}
                >
                    <Heart className={cn("h-4 w-4", wished && "fill-current")} />
                </button>
                {outOfStock ? (
                    <div className="absolute inset-0 grid place-items-center bg-background/70 backdrop-blur-sm">
                        <span className="rounded-full bg-foreground px-3 py-1 text-xs font-bold uppercase tracking-wide text-white">
                            Out of stock
                        </span>
                    </div>
                ) : null}
            </div>

            {/* Body */}
            <div className="flex flex-1 flex-col gap-1.5 p-3">
                <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                    {product.brand}
                </p>
                <p className="line-clamp-2 min-h-[2.5rem] text-sm font-semibold text-foreground">
                    {product.name}
                </p>
                <p className="text-[11px] text-muted-foreground">
                    {product.weight}
                </p>

                <div className="mt-0.5">
                    <StarRating value={product.rating} count={product.rating_count} />
                </div>

                <div className="mt-1 flex items-baseline gap-1.5">
                    <span className="text-base font-extrabold text-foreground">
                        {formatINR(product.price)}
                    </span>
                    {discount > 0 ? (
                        <>
                            <span className="text-[11px] font-medium text-muted-foreground line-through">
                                {formatINR(product.mrp)}
                            </span>
                            <span className="ml-auto text-[11px] font-bold text-brand">
                                {discount}% OFF
                            </span>
                        </>
                    ) : null}
                </div>

                {lowStock ? (
                    <p className="text-[11px] font-semibold text-orange-600">
                        Only {product.stock_count} left
                    </p>
                ) : null}

                {/* Cart control */}
                <div className="mt-2">
                    {qty === 0 ? (
                        <button
                            type="button"
                            onClick={onAdd}
                            disabled={outOfStock}
                            data-testid={`product-add-${product.id}`}
                            className={cn(
                                "inline-flex h-9 w-full items-center justify-center gap-1 rounded-full border text-xs font-bold uppercase tracking-wide transition",
                                outOfStock
                                    ? "cursor-not-allowed border-border bg-muted text-muted-foreground"
                                    : "border-brand/40 bg-brand/10 text-brand hover:bg-brand hover:text-white",
                            )}
                        >
                            <ShoppingCart className="h-3.5 w-3.5" /> Add
                        </button>
                    ) : (
                        <div
                            data-testid={`product-qty-${product.id}`}
                            className="grid h-9 grid-cols-3 items-center overflow-hidden rounded-full bg-brand text-white"
                        >
                            <button
                                type="button"
                                onClick={onDec}
                                aria-label="Decrease quantity"
                                data-testid={`product-dec-${product.id}`}
                                className="grid h-full w-full place-items-center transition hover:bg-brand-700"
                            >
                                <Minus className="h-3.5 w-3.5" />
                            </button>
                            <span className="text-center text-sm font-bold">
                                {qty}
                            </span>
                            <button
                                type="button"
                                onClick={onInc}
                                aria-label="Increase quantity"
                                data-testid={`product-inc-${product.id}`}
                                className="grid h-full w-full place-items-center transition hover:bg-brand-700"
                            >
                                <Plus className="h-3.5 w-3.5" />
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </Link>
    );
}

export default ProductCard;
